import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SuperAdminService from "../../../../services/SuperAdminService";
import EditAndViewButton from "../../../common/EditAndViewButton";
import DeleteButton from "../../../common/DeleteButton";
import SAModal from "../../commonFile/SAModal";

function MachineManagementModal({ school, onClose, onToast }) {
  const { t } = useTranslation();
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [active, setActive] = useState(true);
  const [editingMachine, setEditingMachine] = useState(null);

  const loadMachines = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await SuperAdminService.getMachinesBySchool(school.id);
      const result = Array.isArray(response)
        ? response
        : response?.content || response?.data;
      if (!Array.isArray(result)) {
        throw new Error(t("failedToLoadMachines"));
      }
      setMachines(result);
    } catch (loadError) {
      const message = loadError?.response?.data?.message || loadError.message || t("failedToLoadMachines");
      setError(message);
      onToastRef.current?.(message, "error");
    } finally {
      setLoading(false);
    }
  }, [school.id, t]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  const resetForm = () => {
    setSerialNumber("");
    setActive(true);
    setEditingMachine(null);
  };

  const startEditing = (machine) => {
    setEditingMachine(machine);
    setSerialNumber(machine.serialNumber || "");
    setActive(Boolean(machine.active));
  };

  const saveMachine = async (event) => {
    event.preventDefault();
    const normalizedSerial = serialNumber.trim();
    if (!normalizedSerial || saving) return;

    setSaving(true);
    setError("");
    try {
      if (editingMachine) {
        await SuperAdminService.updateMachine(
          editingMachine.id,
          school.id,
          normalizedSerial,
          active
        );
        onToastRef.current?.(t("machineUpdated"), "success");
      } else {
        await SuperAdminService.createMachine(school.id, normalizedSerial, active);
        onToastRef.current?.(t("machineAdded"), "success");
      }
      resetForm();
      await loadMachines();
    } catch (saveError) {
      const message = saveError?.response?.data?.message || saveError.message || t("machineSaveFailed");
      setError(message);
      onToastRef.current?.(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteMachine = async (machineId) => {
    try {
      await SuperAdminService.deleteMachine(machineId);
      onToastRef.current?.(t("machineDeleted"), "success");
      await loadMachines();
    } catch (deleteError) {
      const message = deleteError?.response?.data?.message || deleteError.message || t("machineDeleteFailed");
      setError(message);
      onToastRef.current?.(message, "error");
    }
  };

  return (
    <SAModal
      title={`🖥️ ${t("manageMachines")} — ${school.schoolName}`}
      onClose={onClose}
      wide
    >
      <form onSubmit={saveMachine}>
        <div className="sa-form-field">
          <label htmlFor="machine-serial-number">{t("machineSerialNumber")}</label>
          <input
            id="machine-serial-number"
            type="text"
            value={serialNumber}
            onChange={(event) => setSerialNumber(event.target.value)}
            placeholder={t("machineSerialNumberPlaceholder")}
            maxLength={100}
            required
            disabled={saving}
          />
        </div>
        <label className="sa-machine-active">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            disabled={saving}
          />
          {t("active")}
        </label>
        <div className="sa-machine-form-actions">
          {editingMachine && (
            <button
              className="sa-btn sa-btn-ghost"
              type="button"
              onClick={resetForm}
              disabled={saving}
            >
              {t("cancel")}
            </button>
          )}
          <button
            className="sa-btn sa-btn-primary"
            type="submit"
            disabled={saving || !serialNumber.trim()}
          >
            {saving ? t("loading") : editingMachine ? t("save") : `+ ${t("addMachine")}`}
          </button>
        </div>
      </form>

      {error && <div className="sa-error-box">{error}</div>}

      <div className="table-wrap sa-machine-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t("machineSerialNumber")}</th>
              <th>{t("status")}</th>
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="text-center">{t("loading")}</td></tr>
            ) : machines.length === 0 ? (
              <tr><td colSpan={4} className="text-center">{t("noMachinesFound")}</td></tr>
            ) : machines.map((machine, index) => (
              <tr key={machine.id}>
                <td>{index + 1}</td>
                <td>{machine.serialNumber || "—"}</td>
                <td>
                  <span className={`sa-status ${machine.active ? "active" : "inactive"}`}>
                    {machine.active ? t("active") : t("inactive")}
                  </span>
                </td>
                <td>
                  <div className="sa-td-actions">
                    <EditAndViewButton
                      icon="✏️"
                      toolTipTitle={t("edit")}
                      onClick={() => startEditing(machine)}
                    />
                    <DeleteButton
                      objectId={machine.id}
                      objectName={machine.serialNumber}
                      itemType="Machine"
                      onDelete={deleteMachine}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SAModal>
  );
}

export default MachineManagementModal;
